import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Shield, Users, Calendar, Filter, AlertTriangle, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [authenticated, setAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [dailyCounts, setDailyCounts] = useState({});
  const [totals, setTotals] = useState({});
  const [filtro, setFiltro] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showDupsOnly, setShowDupsOnly] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const fetchData = useCallback(async (f, d, h) => {
    try {
      const params = { token };
      if (f && f !== "todos" && f !== "rango") params.filtro = f;
      if (f === "rango") { if (d) params.desde = d; if (h) params.hasta = h; }

      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { params: { token } }),
        axios.get(`${API}/admin/leads`, { params }),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data.leads);
      setDailyCounts(leadsRes.data.por_dia);
      setTotals({ total: leadsRes.data.total, unicos: leadsRes.data.unicos, duplicados: leadsRes.data.duplicados });
      setAuthenticated(true);
      setSelected(new Set());
    } catch (e) {
      if (e.response?.status === 401) setError("Token invalido");
      else setError("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData(filtro, desde, hasta);
    else { setError("Token requerido"); setLoading(false); }
  }, [token, fetchData, filtro, desde, hasta]);

  const applyFilter = (f) => { setFiltro(f); setLoading(true); fetchData(f, desde, hasta); };
  const applyRange = () => { setFiltro("rango"); setLoading(true); fetchData("rango", desde, hasta); };

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { params: { token } });
      setLeads(leads.filter(l => l.id !== id));
      setTotals(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (e) { alert("Error al eliminar"); }
  };

  const bulkDelete = async (ids) => {
    if (!window.confirm(`Eliminar ${ids.length} registros?`)) return;
    try {
      await axios.post(`${API}/admin/leads/bulk-delete?token=${token}`, ids);
      setLeads(leads.filter(l => !ids.includes(l.id)));
      setTotals(prev => ({ ...prev, total: prev.total - ids.length }));
      setSelected(new Set());
    } catch (e) { alert("Error al eliminar"); }
  };

  const deleteAllDuplicates = () => {
    const dupIds = leads.filter(l => l.es_duplicado).map(l => l.id);
    if (dupIds.length === 0) { alert("No hay duplicados"); return; }
    bulkDelete(dupIds);
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    const visible = displayedLeads.map(l => l.id);
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible));
  };

  const filteredBySearch = leads.filter(l => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (l.nombre||"").toLowerCase().includes(q) || (l.rut||"").toLowerCase().includes(q) || (l.telefono||"").includes(q) || (l.correo||"").toLowerCase().includes(q) || (l.monto||"").includes(q);
  });
  const displayedLeads = showDupsOnly ? filteredBySearch.filter(l => l.es_duplicado || l.tiene_duplicados) : filteredBySearch;

  const exportCSV = () => {
    const header = "Fecha,Nombre,RUT,Telefono,Correo,Monto,Duplicado\n";
    const rows = leads.map(l =>
      `${l.timestamp?.slice(0,10)},${l.nombre},${l.rut},${l.telefono},${l.correo},${l.monto},${l.es_duplicado ? "Si" : "No"}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "leads_crediacceso.csv"; a.click();
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <p className="text-slate-500">Cargando...</p>
    </div>
  );

  if (error || !authenticated) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-red-200 p-8 text-center max-w-sm">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error || "Acceso denegado"}</p>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              CrediAcceso Admin
            </span>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportCSV} variant="outline" className="rounded-xl text-sm h-9">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total", value: stats.total, color: "bg-slate-900" },
              { label: "Hoy", value: stats.hoy, color: "bg-emerald-500" },
              { label: "Semana", value: stats.semana, color: "bg-blue-500" },
              { label: "Mes", value: stats.mes, color: "bg-purple-500" },
              { label: "Duplicados", value: totals.duplicados, color: "bg-amber-500" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{s.label}</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>{s.value}</p>
                <div className={`w-8 h-1 ${s.color} rounded-full mt-2`} />
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">Filtros</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { key: "todos", label: "Todos" },
              { key: "hoy", label: "Hoy" },
              { key: "semana", label: "Esta semana" },
              { key: "mes", label: "Este mes" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => applyFilter(f.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  filtro === f.key ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => setShowDupsOnly(!showDupsOnly)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                showDupsOnly ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100"
              }`}
            >
              Solo duplicados ({totals.duplicados})
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-slate-400 block mb-1">Buscar</label>
              <Input
                type="text"
                placeholder="Nombre, RUT, telefono, correo..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="rounded-xl h-9"
                data-testid="admin-search"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Desde</label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="rounded-xl h-9 w-40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Hasta</label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="rounded-xl h-9 w-40" />
            </div>
            <Button onClick={applyRange} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-9 text-sm">
              Aplicar rango
            </Button>
          </div>
        </div>

        {/* Daily breakdown */}
        {Object.keys(dailyCounts).length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">Registros por dia</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dailyCounts).sort((a, b) => b[0].localeCompare(a[0])).map(([day, count]) => (
                <div key={day} className="bg-slate-50 rounded-xl px-4 py-2 text-center">
                  <p className="text-xs text-slate-400">{day}</p>
                  <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{count}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bulk actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600">
              <strong>{displayedLeads.length}</strong> registros ({totals.unicos} unicos, {totals.duplicados} duplicados)
            </span>
          </div>
          <div className="flex gap-2">
            {selected.size > 0 && (
              <Button
                onClick={() => bulkDelete(Array.from(selected))}
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl h-9 text-sm"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar seleccionados ({selected.size})
              </Button>
            )}
            {totals.duplicados > 0 && (
              <Button
                onClick={deleteAllDuplicates}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl h-9 text-sm"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar todos los duplicados ({totals.duplicados})
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-3 w-10">
                    <Checkbox checked={selected.size === displayedLeads.length && displayedLeads.length > 0} onCheckedChange={toggleSelectAll} />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Nombre</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">RUT</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Telefono</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Correo</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Monto</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Estado</th>
                  <th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {displayedLeads.map((l) => (
                  <tr key={l.id} className={`border-b border-slate-100 hover:bg-slate-50 ${l.es_duplicado ? "bg-red-50/60" : l.tiene_duplicados ? "bg-amber-50/40" : ""}`}>
                    <td className="px-3 py-3">
                      <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} />
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                      {new Date(l.timestamp).toLocaleDateString("es-CL")} {new Date(l.timestamp).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{l.nombre}</td>
                    <td className="px-4 py-3 text-slate-600">{l.rut}</td>
                    <td className="px-4 py-3 text-slate-600">{l.telefono}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{l.correo}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-600">{l.monto}</td>
                    <td className="px-4 py-3">
                      {l.es_duplicado ? (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Duplicado</span>
                      ) : l.tiene_duplicados ? (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Original</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Unico</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => deleteLead(l.id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {displayedLeads.length === 0 && (
                  <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-400">No hay registros</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
